import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { useFacilities } from "../../facilities/useFacilities.js";
import { excludeArchived, facilityPathLabel } from "../../facilities/facility-tree.js";
import { QrLabelsView } from "../../facilities/qr/QrLabelsView.js";

/** Settings ▸ Print QR labels (ADR-029 in its legacy home, ADR-032): the caller's ACTIVE facilities, every realm. */
export function QrLabelsPage({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const facilities = useFacilities(session);
  const flat = useMemo(() => facilities.data ?? [], [facilities.data]);
  const active = useMemo(() => excludeArchived(flat), [flat]);
  const byId = useMemo(() => new Map(flat.map((f) => [f.id, { name: f.name, parentId: f.parentId }])), [flat]);
  if (facilities.isPending) {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t("workspace.loadingFacilities")}
      </p>
    );
  }
  if (facilities.isError) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-destructive text-sm">
          {t("workspace.couldntLoadTheFacilities")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void facilities.refetch()}>
          {t("workspace.tryAgain")}
        </Button>
      </div>
    );
  }
  return <QrLabelsView realm={session.realm} facilities={active} pathLabelOf={(id) => facilityPathLabel(byId, id)} />;
}
